import classNames from "classnames";
import * as React from "react";

import { FilterInput, type FilterInputControls } from "../Sidebar/FilterInput";
import { useEphemeralWorkspaceStateLoading } from "../WorkspaceCtx";

import { EMPTY_KEYWORD_FILTER, type KeywordFilter } from "./filter";
import styles from "./Topics.module.css";
import { TopicTree, TopicTreeSkeleton } from "./TopicTree";
import { useTopicFilterRequest } from "./useTopicFilterRequest";

interface TopicsProps {
  fileId: string | undefined;
  className?: classNames.Argument;
  header?: React.ReactNode;
}

export function Topics(props: TopicsProps) {
  const { fileId, header } = props;

  const externalFilterRequest = useTopicFilterRequest();

  const [filter, setFilter] =
    React.useState<KeywordFilter>(EMPTY_KEYWORD_FILTER);

  // Imperative handle for FilterInput controls (set term, clear regex toggle).
  const filterInputRef = React.useRef<FilterInputControls>(null);

  const isDataLoading = useEphemeralWorkspaceStateLoading();

  React.useEffect(
    function applyExternalFilterRequest() {
      if (!externalFilterRequest) {
        return;
      }

      // Drive the controlled input from the outside.
      filterInputRef.current?.setTerm(externalFilterRequest.term);

      // Reset regex toggle when an external request populates the input.
      // External requests are always literal terms today; preserving regex mode
      // would risk a literal "deep link term" being interpreted as regex.
      filterInputRef.current?.clearRegex();

      setFilter({
        type: "keyword",
        term: externalFilterRequest.term,
        isRegex: false,
      });
    },
    [externalFilterRequest],
  );

  const handleFilterChange = React.useCallback((filter: KeywordFilter) => {
    setFilter(filter);
  }, []);

  return (
    <div className={classNames(props.className, styles.container)}>
      {header}
      <FilterInput
        controlsRef={filterInputRef}
        className={styles.filter}
        onFilterChange={handleFilterChange}
      />
      {isDataLoading ? (
        <TopicTreeSkeleton estimatedTopicCount={15} />
      ) : (
        <TopicTree fileId={fileId} filter={filter} />
      )}
    </div>
  );
}
